import { z } from "zod";

import type { PgnProviderType } from "@/gen/graphql";

export const PGN_PROVIDER_TYPES: [PgnProviderType, ...PgnProviderType[]] = [
    "POLL_PGN",
    "STREAMING_PGN",
    "MANUAL",
    "LIVECHESS_CLOUD_ROUND",
];

export const zRoundInput = z.object({
    title: z.string(),
    evaluationSurgeProtectionOverride: z.boolean().optional().nullable(),
    scheduledStartAt: z
        .string()
        .or(z.date())
        .transform(arg => new Date(arg)),
    lastGameScheduledStartAt: z
        .string()
        .or(z.date())
        .transform(arg => new Date(arg))
        .nullable(),
    roundSyncerConfig: z
        .object({
            pgnProviderType: z.enum(PGN_PROVIDER_TYPES),
            pollPgnSource: z.string().url("Poll PGN source needs to be an URL").optional(),
            streamingPgnSource: z
                .string()
                .url("Streaming PGN source needs to be an URL")
                .optional(),
            pollStart: z
                .string()
                .or(z.date())
                .transform(arg => new Date(arg))
                .optional()
                .nullable(),
            pollEnd: z
                .string()
                .or(z.date())
                .transform(arg => new Date(arg))
                .optional()
                .nullable(),
            pollCreateGames: z.boolean().optional().nullable(),
            livechesscloudRoundId: z.string().optional().nullable(),
            livechesscloudRoundNumber: z.number().int().positive().optional().nullable(),
            pgnDelaySecs: z.number().int().min(0).optional().nullable(),
        })
        .refine(
            data => {
                // Only validate poll times for active provider types (not manual)
                if (data.pgnProviderType !== "MANUAL" && data.pollEnd && data.pollStart) {
                    return data.pollEnd > data.pollStart;
                }
                return true;
            },
            {
                path: ["pollEnd"],
                message: "Poll end must be after poll start",
            }
        )
        .refine(
            data => {
                // Ensure the appropriate PGN source is provided based on provider type
                if (data.pgnProviderType === "POLL_PGN") {
                    return data.pollPgnSource && data.pollPgnSource.length > 0;
                }
                if (data.pgnProviderType === "STREAMING_PGN") {
                    return data.streamingPgnSource && data.streamingPgnSource.length > 0;
                }
                // Manual mode doesn't require any PGN sources
                if (data.pgnProviderType === "MANUAL") {
                    return true;
                }
                return true;
            },
            {
                path: ["pollPgnSource"],
                message: "Please provide the appropriate PGN source for the selected provider type",
            }
        )
        .refine(
            data => {
                // Ensure polling fields are provided for active provider types
                if (
                    data.pgnProviderType === "POLL_PGN" ||
                    data.pgnProviderType === "STREAMING_PGN" ||
                    data.pgnProviderType === "LIVECHESS_CLOUD_ROUND"
                ) {
                    return !!(
                        data.pollStart &&
                        data.pollEnd &&
                        typeof data.pollCreateGames === "boolean"
                    );
                }
                // Manual mode doesn't require polling fields
                return true;
            },
            {
                path: ["pollStart"],
                message:
                    "Poll start, end, and create games settings are required for active provider types",
            }
        )
        .refine(
            data => {
                // Ensure LivechessCloud fields are provided when that provider type is selected
                if (data.pgnProviderType === "LIVECHESS_CLOUD_ROUND") {
                    return !!(
                        data.livechesscloudRoundId &&
                        data.livechesscloudRoundId.length > 0 &&
                        data.livechesscloudRoundNumber &&
                        data.livechesscloudRoundNumber > 0
                    );
                }
                return true;
            },
            {
                path: ["livechesscloudRoundId"],
                message:
                    "Livechess Cloud Round ID and Round Number are required for Livechess Cloud Round provider type",
            }
        )
        .optional()
        .nullable(),
});

export type RoundInput = z.infer<typeof zRoundInput>;
